import { TargetStatusEnum } from '@/types/c2/c2.types';
import { MarkEnum } from '@/types/commons/commons.types';
import {
  Procedure,
  ProcedureStateEnum,
  ProcedureStep,
  ProcedureStepStateEnum,
  ProcedureStepTypeEnum,
} from '@/types/config/procedure.types';
import {
  AlertContextEnum,
  AlertTypeEnum,
  DroneAlertLevelEnum,
  DroneNotification,
  DroneNotificationLight,
  ManualAlert,
  NotificationStatusEnum,
  NotificationTypeEnum,
  SegmentAlertLevelEnum,
  SegmentNotification,
  SensorAlertLevelEnum,
  SensorFailureAlert,
  SensorNotification,
  SpaceViolationAlert,
  VerticalTrespassEnum,
} from '@/types/data/data.types';
import { ClassificationEnum } from '@/types/sensor/classification.types';
import { ReliabilityEnum } from '@/types/sensor/identification.types';
import {
  AutomatonSegmentTypeEnum,
  ConnectorStatusEnum,
  HealthStatusEnum,
  SensorTypeEnum,
} from '@/types/sensor/sensors';
import { DroneClearanceEnum } from '@/types/utm/drone.types';

import { PLATFORM } from './config.constants';

export const DRONE_NOTIFICATION_WARNING: DroneNotificationLight = {
  id: '0',
  site: 'local',
  identifier: 'drone xyz',
  displayId: null,
  type: NotificationTypeEnum.DRONE,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:10:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-01-20T15:08:13.721Z',
  lastProcedureClosingTime: null,
  occurrenceCount: 12,
  alertCount: 3,
  worstAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  currentWorstAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  lastAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  currentHeight: 50,
  maxTransmittedAgl: 150,
  maxComputedAgl: 150,
  maxTransmittedAsl: 150,
  maxComputedAsl: 150,
  lastAltitude: 50,
  targetStatus: TargetStatusEnum.ONGOING,
  classification: ClassificationEnum.SUSPECT,
  hasFlightPlan: true,
  hasHadActiveFlightPlan: true,
  withRemoteControl: true,
  hasDetectedRemoteControl: true,
  trackIds: [
    {
      id: '0',
      uuid: 'uuid1',
      sensorCode: 'code',
      sensorType: SensorTypeEnum.ADSB,
      appCode: 'sensor-local',
      site: 'local',
      uniqueKey: 'local_sensor-local-code',
    },
  ],
  reliability: ReliabilityEnum.SURE,
  clearance: DroneClearanceEnum.FULL_CLEARANCE,
  lastDetectionTime: '2025-01-20T19:08:13.721Z',
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  startPosition: { latitude: 46, longitude: 1, type: '2D' },
  closable: true,
  muted: true,
  organization: 'holo',
};

export const DRONE_NOTIFICATION_CRITICAL: DroneNotificationLight = {
  id: '1',
  site: 'local',
  identifier: 'drone xyz',
  displayId: null,
  occurrenceCount: 1,
  type: NotificationTypeEnum.DRONE,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:09:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-01-20T15:09:13.721Z',
  lastProcedureClosingTime: null,
  alertCount: 3,
  worstAlertLevel: DroneAlertLevelEnum.DRONE_CRITICAL,
  currentWorstAlertLevel: DroneAlertLevelEnum.DRONE_CRITICAL,
  lastAlertLevel: DroneAlertLevelEnum.DRONE_INFO,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  currentHeight: 50,
  maxTransmittedAgl: 150,
  maxComputedAgl: 150,
  maxTransmittedAsl: 150,
  maxComputedAsl: 150,
  lastAltitude: 50,
  targetStatus: TargetStatusEnum.ONGOING,
  classification: ClassificationEnum.ENEMY,
  hasFlightPlan: true,
  hasHadActiveFlightPlan: true,
  withRemoteControl: false,
  hasDetectedRemoteControl: false,
  trackIds: [
    {
      id: '0',
      uuid: 'uuid1',
      sensorCode: 'code',
      sensorType: SensorTypeEnum.ADSB,
      appCode: 'sensor_local',
      site: 'local',
      uniqueKey: 'local_sensor-local-code',
    },
  ],
  reliability: ReliabilityEnum.SURE,
  clearance: DroneClearanceEnum.NO_CLEARANCE,
  lastDetectionTime: '2025-01-21T13:08:13.721Z',
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  startPosition: { latitude: 46, longitude: 1, type: '2D' },
  closable: true,
  muted: true,
  organization: 'holo',
};

export const DRONE_NOTIFICATION_CRITICAL_UNSURE: DroneNotificationLight = {
  ...DRONE_NOTIFICATION_CRITICAL,
  reliability: ReliabilityEnum.UNSURE,
};

export const DRONE_NOTIFICATION_INFO: DroneNotificationLight = {
  id: '2',
  site: 'local',
  identifier: 'drone xyz',
  displayId: null,
  type: NotificationTypeEnum.DRONE,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:08:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-01-20T13:08:14.721Z',
  lastProcedureClosingTime: null,
  occurrenceCount: 2,
  alertCount: 3,
  worstAlertLevel: DroneAlertLevelEnum.DRONE_INFO,
  currentWorstAlertLevel: DroneAlertLevelEnum.DRONE_INFO,
  lastAlertLevel: DroneAlertLevelEnum.DRONE_INFO,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  currentHeight: 50,
  maxTransmittedAgl: 150,
  maxComputedAgl: 150,
  maxTransmittedAsl: 150,
  maxComputedAsl: 150,
  lastAltitude: 50,
  targetStatus: TargetStatusEnum.ONGOING,
  classification: ClassificationEnum.WATCH,
  hasFlightPlan: true,
  hasHadActiveFlightPlan: true,
  withRemoteControl: false,
  hasDetectedRemoteControl: false,
  trackIds: [
    {
      id: '0',
      uuid: 'uuid1',
      sensorCode: 'code',
      sensorType: SensorTypeEnum.ADSB,
      appCode: 'sensor_local',
      site: 'local',
      uniqueKey: 'local_sensor-local-code',
    },
  ],
  reliability: ReliabilityEnum.SURE,
  clearance: DroneClearanceEnum.FULL_CLEARANCE_WITHIN_FLIGHTPLAN,
  lastDetectionTime: '2025-02-20T13:08:15.721Z',
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  startPosition: { latitude: 46, longitude: 1, type: '2D' },
  closable: true,
  muted: false,
  organization: 'holo',
};

export const FULL_DRONE_NOTIFICATION_CRITICAL: DroneNotification = {
  ...DRONE_NOTIFICATION_CRITICAL,
  lastAltitude: 9,
  elevationAgl: null,
  elevationAsl: null,
  trajectory: [
    {
      latitude: 48,
      longitude: 2,
      altitude: 9,
      type: '3D',
    },
    {
      latitude: 48,
      longitude: 3,
      altitude: 9,
      type: '3D',
    },
  ],
};

export const PERIMETER_EFFRACTION_NOTIFICATION: SegmentNotification = {
  id: '3',
  site: 'local',
  identifier: 'effraction x',
  type: NotificationTypeEnum.SEGMENT,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:07:33.721Z',
  endTime: '2025-01-21T13:06:13.721Z',
  lastProcedureFirstAlertStartTime: '2025-01-20T15:08:13.721Z',
  lastProcedureClosingTime: '2025-01-21T16:06:13.721Z',
  alertCount: 3,
  segmentName: 'segmentA',
  cabinetName: 'cabinetA',
  worstAlertLevel: SegmentAlertLevelEnum.PERIMETER_EFFRACTION,
  currentWorstAlertLevel: SegmentAlertLevelEnum.PERIMETER_EFFRACTION,
  lastAlertLevel: SegmentAlertLevelEnum.PERIMETER_EFFRACTION,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
    {
      id: 1,
      type: MarkEnum.PLATFORM,
      context: 'CDG1',
    },
  ],
  status: NotificationStatusEnum.PENDING,
  segmentStatus: HealthStatusEnum.HEALTHY,
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: null,
  currentStepType: null,
  nbSteps: 3,
  segmentType: AutomatonSegmentTypeEnum.LOWER,
  closable: true,
  muted: true,
  organization: 'holo',
};

export const SENSOR_DISCONNECTED_NOTIFICATION: SensorNotification = {
  id: '6',
  site: 'local',
  identifier: 'disconnected x',
  sensorName: 'Aeroscope',
  type: NotificationTypeEnum.SENSOR,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T12:05:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-01-20T15:08:13.721Z',
  lastProcedureClosingTime: null,
  alertCount: 3,
  worstAlertLevel: SensorAlertLevelEnum.SENSOR_DISCONNECTED,
  currentWorstAlertLevel: SensorAlertLevelEnum.SENSOR_DISCONNECTED,
  lastAlertLevel: SensorAlertLevelEnum.SENSOR_DISCONNECTED,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  sensorType: SensorTypeEnum.AEROSCOPE,
  sensorStatus: ConnectorStatusEnum.UNREACHABLE,
  procedureState: ProcedureStateEnum.IN_PROGRESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  closable: true,
  muted: false,
  organization: 'holo',
};

export const SENSOR_KO_NOTIFICATION: SensorNotification = {
  ...SENSOR_DISCONNECTED_NOTIFICATION,
  identifier: 'ko x',
  sensorStatus: HealthStatusEnum.KO,
  worstAlertLevel: SensorAlertLevelEnum.SENSOR_FAILURE,
};

export const SENSOR_FAILURE_NOTIFICATION: SensorNotification = {
  id: '7',
  site: 'local',
  identifier: 'failure x',
  sensorName: 'Aeroscope',
  type: NotificationTypeEnum.SENSOR,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T12:04:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-01-20T15:08:13.721Z',
  lastProcedureClosingTime: null,
  alertCount: 3,
  worstAlertLevel: SensorAlertLevelEnum.SENSOR_FAILURE,
  currentWorstAlertLevel: SensorAlertLevelEnum.SENSOR_FAILURE,
  lastAlertLevel: SensorAlertLevelEnum.SENSOR_FAILURE,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  sensorType: SensorTypeEnum.AEROSCOPE,
  sensorStatus: HealthStatusEnum.DEGRADED,
  procedureState: ProcedureStateEnum.IN_PROGRESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  closable: true,
  muted: true,
  organization: 'holo',
};

export const AUTOMATON_DISCONNECTED_NOTIFICATION: SensorNotification = {
  id: '6',
  site: 'local',
  identifier: 'automaton_disconnected x',
  sensorName: 'Automaton',
  type: NotificationTypeEnum.SENSOR,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-09-03T12:05:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-09-03T15:08:13.721Z',
  lastProcedureClosingTime: null,
  alertCount: 3,
  worstAlertLevel: SensorAlertLevelEnum.SENSOR_DISCONNECTED,
  currentWorstAlertLevel: SensorAlertLevelEnum.SENSOR_DISCONNECTED,
  lastAlertLevel: SensorAlertLevelEnum.SENSOR_DISCONNECTED,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  sensorType: SensorTypeEnum.AUTOMATON,
  sensorStatus: HealthStatusEnum.KO,
  procedureState: ProcedureStateEnum.IN_PROGRESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  closable: true,
  muted: true,
  organization: 'holo',
};

export const AUTOMATON_FAILURE_NOTIFICATION: SensorNotification = {
  id: '9',
  site: 'local',
  identifier: 'automaton_failure x',
  sensorName: 'Automaton',
  type: NotificationTypeEnum.SENSOR,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-09-03T12:04:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-09_03T15:08:13.721Z',
  lastProcedureClosingTime: null,
  alertCount: 3,
  worstAlertLevel: SensorAlertLevelEnum.SENSOR_FAILURE,
  currentWorstAlertLevel: SensorAlertLevelEnum.SENSOR_FAILURE,
  lastAlertLevel: SensorAlertLevelEnum.SENSOR_FAILURE,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  sensorType: SensorTypeEnum.AUTOMATON,
  sensorStatus: HealthStatusEnum.DEGRADED,
  procedureState: ProcedureStateEnum.IN_PROGRESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  closable: true,
  muted: true,
  organization: 'holo',
};

export const SPACE_VIOLATION_ALERT: SpaceViolationAlert = {
  id: 0,
  site: 'local',
  identifier: 'alert',
  startTime: new Date().toISOString(),
  type: AlertTypeEnum.SPACE_VIOLATION,
  context: AlertContextEnum.OUTSIDE_FLIGHT_PLAN,
  level: DroneAlertLevelEnum.DRONE_CRITICAL,
  endTime: null,
  parentId: '1',
  procedureId: '1',
  zoneId: 1,
  zoneName: 'Zone critique',
  heightMin: 0,
  heightMax: 100,
  marks: [],
  verticalTrespass: VerticalTrespassEnum.ABOVE_MAX_HEIGHT,
};

export const SENSOR_FAILURE_ALERT: SensorFailureAlert = {
  id: 1,
  site: 'local',
  identifier: 'alert',
  startTime: new Date().toISOString(),
  type: AlertTypeEnum.SENSOR_FAILURE,
  context: AlertContextEnum.SENSOR_FAILURE,
  level: SensorAlertLevelEnum.SENSOR_FAILURE,
  endTime: null,
  parentId: '1',
  procedureId: '1',
  marks: [],
};

export const TACTICAL_CLEARANCE_ALERT: ManualAlert = {
  id: 2,
  site: 'local',
  identifier: 'alert',
  startTime: new Date().toISOString(),
  type: AlertTypeEnum.MANUAL,
  context: AlertContextEnum.TACTICAL_CLEARANCE,
  level: DroneAlertLevelEnum.DRONE_CRITICAL,
  endTime: null,
  parentId: '1',
  procedureId: '1',
  marks: [],
};

export const PROCEDURE_ACK_STEP: ProcedureStep = {
  id: 0,
  code: 'step1',
  type: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  state: ProcedureStepStateEnum.CONFIRMED,
  mandatory: true,
  rank: 0,
  phoneNumber: null,
  instructions: [],
  modifiedBy: 'killian',
  modificationTime: new Date().toISOString(),
};

export const PROCEDURE: Procedure = {
  id: '0',
  name: 'code',
  site: 'local',
  state: ProcedureStateEnum.IN_PROGRESS,
  steps: [
    PROCEDURE_ACK_STEP,
    {
      id: 1,
      code: 'step2',
      type: ProcedureStepTypeEnum.CONFIRMATION,
      state: ProcedureStepStateEnum.DENIED,
      mandatory: true,
      rank: 1,
      phoneNumber: null,
      instructions: [],
      modifiedBy: 'killian',
      modificationTime: new Date().toISOString(),
    },
    {
      id: 2,
      code: 'step3',
      type: ProcedureStepTypeEnum.PHONE_CALL,
      state: ProcedureStepStateEnum.IN_PROGRESS,
      mandatory: false,
      rank: 2,
      phoneNumber: '012345678',
      instructions: [
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
      ],
      modifiedBy: 'killian',
      modificationTime: new Date().toISOString(),
    },
    {
      id: 3,
      code: 'step4',
      type: ProcedureStepTypeEnum.CLOSING,
      state: ProcedureStepStateEnum.TO_BE_STARTED,
      mandatory: true,
      rank: 3,
      phoneNumber: null,
      instructions: [],
      modifiedBy: null,
      modificationTime: null,
    },
    {
      id: 4,
      code: 'step5',
      type: ProcedureStepTypeEnum.CLOSING,
      state: ProcedureStepStateEnum.TO_BE_STARTED,
      mandatory: true,
      rank: 4,
      phoneNumber: null,
      instructions: [
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
        "Ceci est une consigne de test pour vérifier que l'affichage est correct",
      ],
      modifiedBy: null,
      modificationTime: null,
    },
  ],
};

export const DRONE_NOTIFICATION_CRITICAL_SURE: DroneNotificationLight = {
  id: '0',
  site: 'local',
  identifier: 'drone xyz',
  displayId: null,
  type: NotificationTypeEnum.DRONE,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:10:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-01-20T15:08:13.721Z',
  lastProcedureClosingTime: null,
  occurrenceCount: 12,
  alertCount: 3,
  worstAlertLevel: DroneAlertLevelEnum.DRONE_CRITICAL,
  currentWorstAlertLevel: DroneAlertLevelEnum.DRONE_CRITICAL,
  lastAlertLevel: DroneAlertLevelEnum.DRONE_CRITICAL,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  currentHeight: 50,
  maxTransmittedAgl: 150,
  maxComputedAgl: 150,
  maxTransmittedAsl: 150,
  maxComputedAsl: 150,
  lastAltitude: 50,
  targetStatus: TargetStatusEnum.ONGOING,
  classification: ClassificationEnum.SUSPECT,
  hasFlightPlan: true,
  hasHadActiveFlightPlan: true,
  withRemoteControl: false,
  hasDetectedRemoteControl: false,
  trackIds: [
    {
      id: '0',
      uuid: 'uuid1',
      sensorCode: 'code',
      sensorType: SensorTypeEnum.ADSB,
      appCode: 'sensor_local',
      site: 'local',
      uniqueKey: 'local_sensor-local-code',
    },
  ],
  reliability: ReliabilityEnum.SURE,
  clearance: DroneClearanceEnum.FULL_CLEARANCE,
  lastDetectionTime: '2025-01-20T19:08:13.721Z',
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  startPosition: { latitude: 46, longitude: 1, type: '2D' },
  closable: true,
  muted: true,
  organization: 'holo',
};

export const DRONE_NOTIFICATION_WARNING_UNSURE: DroneNotificationLight = {
  id: '0',
  site: 'local',
  identifier: 'drone xyz',
  displayId: null,
  type: NotificationTypeEnum.DRONE,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:10:13.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-01-20T15:08:13.721Z',
  lastProcedureClosingTime: null,
  occurrenceCount: 12,
  alertCount: 3,

  worstAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  currentWorstAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  lastAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  currentHeight: 50,
  maxTransmittedAgl: 150,
  maxComputedAgl: 150,
  maxTransmittedAsl: 150,
  maxComputedAsl: 150,
  lastAltitude: 50,
  targetStatus: TargetStatusEnum.ONGOING,
  classification: ClassificationEnum.SUSPECT,
  hasFlightPlan: true,
  hasHadActiveFlightPlan: true,
  withRemoteControl: false,
  hasDetectedRemoteControl: false,
  trackIds: [
    {
      id: '0',
      uuid: 'uuid1',
      sensorCode: 'code',
      sensorType: SensorTypeEnum.ADSB,
      appCode: 'sensor_local',
      site: 'local',
      uniqueKey: 'local_sensor-local-code',
    },
  ],
  reliability: ReliabilityEnum.UNSURE,
  clearance: DroneClearanceEnum.FULL_CLEARANCE,
  lastDetectionTime: '2025-01-20T19:08:13.721Z',
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  startPosition: { latitude: 46, longitude: 1, type: '2D' },
  closable: true,
  muted: true,
  organization: 'holo',
};

export const DRONE_NOTIFICATION_WARNING_EXPIRED: DroneNotificationLight = {
  id: '0',
  site: 'local',
  identifier: 'drone xyz',
  displayId: null,
  type: NotificationTypeEnum.DRONE,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:10:13.721Z',
  endTime: '2025-01-20T13:11:13.721Z',
  lastProcedureFirstAlertStartTime: '2025-01-20T13:10:30.721Z',
  lastProcedureClosingTime: '2025-01-20T13:10:55.721Z',
  occurrenceCount: 12,
  alertCount: 3,
  worstAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  currentWorstAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  lastAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  currentHeight: 50,
  maxTransmittedAgl: 150,
  maxComputedAgl: 150,
  maxTransmittedAsl: 150,
  maxComputedAsl: 150,
  lastAltitude: 50,
  targetStatus: TargetStatusEnum.ONGOING,
  classification: ClassificationEnum.SUSPECT,
  hasFlightPlan: true,
  hasHadActiveFlightPlan: true,
  withRemoteControl: false,
  hasDetectedRemoteControl: false,
  trackIds: [
    {
      id: '0',
      uuid: 'uuid1',
      sensorCode: 'code',
      sensorType: SensorTypeEnum.ADSB,
      appCode: 'sensor_local',
      site: 'local',
      uniqueKey: 'local_sensor-local-code',
    },
  ],
  reliability: ReliabilityEnum.UNSURE,
  clearance: DroneClearanceEnum.FULL_CLEARANCE,
  lastDetectionTime: '2025-01-20T19:08:13.721Z',
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  startPosition: { latitude: 46, longitude: 1, type: '2D' },
  closable: true,
  muted: true,
  organization: 'holo',
};

export const DRONE_NOTIFICATION_CRITICAL_EXPIRED: DroneNotificationLight = {
  id: '0',
  site: 'local',
  identifier: 'drone xyz',
  displayId: null,
  type: NotificationTypeEnum.DRONE,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:10:13.721Z',
  endTime: '2025-01-20T13:11:13.721Z',
  lastProcedureFirstAlertStartTime: '2025-01-20T13:11:03.721Z',
  lastProcedureClosingTime: null,
  occurrenceCount: 12,
  alertCount: 3,
  worstAlertLevel: DroneAlertLevelEnum.DRONE_CRITICAL,
  currentWorstAlertLevel: DroneAlertLevelEnum.DRONE_CRITICAL,
  lastAlertLevel: DroneAlertLevelEnum.DRONE_CRITICAL,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  currentHeight: 50,
  maxTransmittedAgl: 150,
  maxComputedAgl: 150,
  maxTransmittedAsl: 150,
  maxComputedAsl: 150,
  lastAltitude: 50,
  targetStatus: TargetStatusEnum.ONGOING,
  classification: ClassificationEnum.SUSPECT,
  hasFlightPlan: true,
  hasHadActiveFlightPlan: true,
  withRemoteControl: false,
  hasDetectedRemoteControl: false,
  trackIds: [
    {
      id: '0',
      uuid: 'uuid1',
      sensorCode: 'code',
      sensorType: SensorTypeEnum.ADSB,
      appCode: 'sensor_local',
      site: 'local',
      uniqueKey: 'local_sensor-local-code',
    },
  ],
  reliability: ReliabilityEnum.UNSURE,
  clearance: DroneClearanceEnum.FULL_CLEARANCE,
  lastDetectionTime: '2025-01-20T19:08:13.721Z',
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  startPosition: { latitude: 46, longitude: 1, type: '2D' },
  closable: true,
  muted: true,
  organization: 'holo',
};

export const DRONE_NOTIFICATION_WARNING_SURE_EARLY: DroneNotificationLight = {
  id: '0',
  site: 'local',
  identifier: 'drone xyz',
  displayId: null,
  type: NotificationTypeEnum.DRONE,
  position: { latitude: 48, longitude: 0, type: '2D' },
  startTime: '2025-01-20T13:10:30.721Z',
  endTime: null,
  lastProcedureFirstAlertStartTime: '2025-01-20T15:08:13.721Z',
  lastProcedureClosingTime: null,
  occurrenceCount: 12,
  alertCount: 3,
  worstAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  currentWorstAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  lastAlertLevel: DroneAlertLevelEnum.DRONE_WARNING,
  marks: [
    {
      id: 0,
      type: MarkEnum.PLATFORM,
      context: PLATFORM.code,
    },
  ],
  status: NotificationStatusEnum.PENDING,
  currentHeight: 50,
  maxTransmittedAgl: 150,
  maxComputedAgl: 150,
  maxTransmittedAsl: 150,
  maxComputedAsl: 150,
  lastAltitude: 50,
  targetStatus: TargetStatusEnum.ONGOING,
  classification: ClassificationEnum.SUSPECT,
  hasFlightPlan: true,
  hasHadActiveFlightPlan: true,
  withRemoteControl: false,
  hasDetectedRemoteControl: false,
  trackIds: [
    {
      id: '0',
      uuid: 'uuid1',
      sensorCode: 'code',
      sensorType: SensorTypeEnum.ADSB,
      appCode: 'sensor_local',
      site: 'local',
      uniqueKey: 'local_sensor-local-code',
    },
  ],
  reliability: ReliabilityEnum.SURE,
  clearance: DroneClearanceEnum.FULL_CLEARANCE,
  lastDetectionTime: '2025-01-20T19:08:13.721Z',
  procedureState: ProcedureStateEnum.TO_PROCESS,
  currentStepRank: 0,
  currentStepType: ProcedureStepTypeEnum.ACKNOWLEDGEMENT,
  nbSteps: 3,
  startPosition: { latitude: 46, longitude: 1, type: '2D' },
  closable: true,
  muted: true,
  organization: 'holo',
};
